/**
 *
 * @module component/master
 * @author Adam Pirani [adam.pirani@mlb.com]
 * @description entry point for all component specific functionality
 * @todo: move default mode & autoplay into a ComponentMaster.defaultOpts property
 *
 */

import isFunction from 'lodash-es/isFunction';

/**
 *
 * @name component
 * @description init, render, addEvents, and destroy methods are required for consistency.
 * The parent viewModel is passed in as a reference, for external communication events can
 * be emitted via the parent
 * The provided clickHandler method is optional and can be removed
 */
class Component {

    constructor(parent = {}) {

        const { opts, container, dataCache } = parent;

        this.element = container;
        this.parent = parent;
        this.dataCache = dataCache;
        this.opts = opts;

        /**
         *
         * @name core
         * @description component master core properties
         *
         */
        this.core = {
            gameInfoAnimationTimeout: null,
            resizeThrottleEvent: null,
            scrollAnimationTimer: null,
            scrollAnimationInProgress: false,
            scrollThrottleEvent: null
        };

        /**
         *
         * @name props
         * @description component master properties
         *
         */
        this.props = {
            componentName: 'g5-component--mlb-scores',
            gameInfoLabel: null,
            inningsScrollLeft: null,
            inningsScrollRight: null,
            inningsContainer: null,
            inningsContainerScrollLeft: undefined,
            inningsContainerMaxLeft: undefined,
            scrollDistance: 84
        };

        // Bind static event handlers
        const { scrollInningsLeftEvent, scrollInningsRightEvent, inningsScrollEvent, windowResizeEvent } = Component;
        const { toggleGameInfoEvent } = Component;

        this.scrollInningsLeftEvent = scrollInningsLeftEvent.bind(this);
        this.scrollInningsRightEvent = scrollInningsRightEvent.bind(this);
        this.inningsScrollEvent = inningsScrollEvent.bind(this);
        this.windowResizeEvent = windowResizeEvent.bind(this);
        this.toggleGameInfoEvent = toggleGameInfoEvent.bind(this);

    }

    /**
     *
     * @method init
     * @param {Object} data
     * @returns {Component} this
     * @description instantiates component with a reference to the parent viewModel, properties on
     * the parent reference should never be modified in any way
     *
     */
    init(data = {}, hasextraInnings = false) {

        let { opts } = this;
        let { mode = 'responsive', device = 'desktop' } = opts;

        this.hasTouch = (mode === 'minisb') && (device !== 'desktop');
        this.dataCache = data;
        this.render(hasextraInnings);

        return this;

    }

    /**
     *
     * @method render
     * @returns {Component} this
     * @description attaches component functionality
     *
     */
    render(hasextraInnings = false) {

        let { opts } = this;
        let { extendListeners } = opts;

        this.addClasses().addEvents(extendListeners, hasextraInnings);
        this.embedVideo();

        return this;

    }

    /**
     *
     * @method embedVideo
     * @description embeds video using mlb-internal-embed passed in as videoEmbedder option
     *
     */
    embedVideo() {

        const { element, opts, props } = this;
        const { videoEmbedder, autoPlay = false, mode = 'responsive', gamePk } = opts;
        const { componentName } = props;

        if (mode === 'responsive' && videoEmbedder) {

            const containerDiv = element.querySelector(`.${componentName}__panel--video`);
            const contentId = containerDiv.getAttribute('data-contentId');

            if (contentId) {

                const panelIsVisible = containerDiv && window.getComputedStyle(containerDiv).display !== 'none';
                const autoPlayVideo = autoPlay && panelIsVisible;

                videoEmbedder.createPlayer({
                    container: `#${componentName}__video--${gamePk}`,
                    autoPlay: autoPlayVideo,
                    mute: !!autoPlayVideo,
                    contentId: contentId
                });

            }
        }
    }

    /**
     *
     * @method animateInningsScroll
     * @desc animate scroll to a direction
     * @param {String} direction
     * @param {Number} distance to scroll in pixels
     * @param {Number} tickPx how many pixels to scroll per click
     *
     */
    animateInningsScroll(direction = 'right', distance = 0, tickPx = 3) {

        let { core, props } = this;
        let { inningsContainer } = props;

        let tick = 1;
        let tickDistance = 0;
        let isLeft = direction === 'left';
        let isRight = direction === 'right';

        //
        // if animation is already in progress, ignore execution
        //
        if (core.scrollAnimationInProgress) {
            return;
        }

        this.pauseRendering();

        //
        // animate scroll to direction
        //
        core.scrollAnimationTimer = setInterval(() => {

            core.scrollAnimationInProgress = true;

            if ((distance - tickDistance) < tickPx) {

                // eslint-disable-next-line no-param-reassign
                tickPx = distance - tickDistance;

            }

            tickDistance += tickPx;

            if (isRight) {
                inningsContainer.scrollLeft += tickPx;
            }

            if (isLeft) {
                inningsContainer.scrollLeft -= tickPx;
            }

            if (tickDistance === distance) {

                core.scrollAnimationInProgress = false;
                clearInterval(core.scrollAnimationTimer);
                this.resumeRendering();

            }

        }, tick);


    }

    /**
     *
     * @method addEvents
     * @desc add component events, will be called any time template is re-rendered
     * @param {Function} cb
     * @param {Boolean} hasExtraInnings
     * @returns {Component} this
     *
     */
    addEvents(cb, hasExtraInnings = false) {

        let { element, props, opts } = this;
        let { toggleGameInfoEvent, scrollInningsLeftEvent, scrollInningsRightEvent, inningsScrollEvent, windowResizeEvent } = this;
        let { mode = 'responsive' } = opts;
        let { componentName } = props;

        const gameInfoLabel = element.querySelector(`.${componentName}__game-info__label`);

        if (gameInfoLabel) {

            gameInfoLabel.addEventListener('click', toggleGameInfoEvent, false);

        }

        props.gameInfoLabel = gameInfoLabel;

        if (!!hasExtraInnings && (mode === 'responsive')) {

            const inningsScrollLeft = element.querySelector(`.${componentName}__linescore__innings-scroll--left`);
            const inningsScrollRight = element.querySelector(`.${componentName}__linescore__innings-scroll--right`);
            const inningsContainer = element.querySelector(`.${componentName}__linescore__table-container--innings`);

            props.inningsScrollLeft = inningsScrollLeft;
            props.inningsScrollRight = inningsScrollRight;
            props.inningsContainer = inningsContainer;

            this.setInningsScrollLeft();
            this.updateScrollButtons();

            if (inningsScrollLeft && inningsScrollRight) {

                inningsScrollLeft.addEventListener('click', scrollInningsLeftEvent, false);
                inningsScrollRight.addEventListener('click', scrollInningsRightEvent, false);

            }

            if (inningsContainer) {

                inningsContainer.addEventListener('scroll', inningsScrollEvent, false);
                window.addEventListener('resize', windowResizeEvent, false);

            }

        }

        if (isFunction(cb)) {
            cb(element);
        }

        return this;

    }

    /**
     *
     * @method addClasses
     * @description toggles modifiers based on device features
     * @returns {Component} this
     *
     */
    addClasses() {

        let { element, opts, props, hasTouch } = this;
        let { componentName } = props;

        // Default mode must match in model/master::defaultOpts.responsive
        let { mode = 'responsive', sportId = 1 } = opts;
        let sport = sportId === 1 ? 'mlb' : 'milb';

        element.classList.add(`${componentName}--${mode}`);
        element.classList.add(`${componentName}--${sport}`);

        if (!hasTouch) {

            element.classList.add(`${componentName}--has-pointer`);

        }

        if (hasTouch) {

            element.classList.add(`${componentName}--has-touch`);

        }

        return this;

    }

    /**
     *
     * @method destroy
     * @returns {Component} this
     * @description detaches component functionality, all events must be cleaned up
     *
     */
    destroy() {

        let { props, core } = this;
        let { toggleGameInfoEvent, scrollInningsLeftEvent, scrollInningsRightEvent, inningsScrollEvent, windowResizeEvent } = this;
        let { gameInfoLabel, inningsScrollLeft, inningsScrollRight, inningsContainer } = props;

        if (gameInfoLabel) {

            gameInfoLabel.removeEventListener('click', toggleGameInfoEvent);

        }

        if (inningsScrollLeft && inningsScrollRight) {

            inningsScrollLeft.removeEventListener('click', scrollInningsLeftEvent);
            inningsScrollRight.removeEventListener('click', scrollInningsRightEvent);

        }

        if (inningsContainer) {

            inningsContainer.removeEventListener('scroll', inningsScrollEvent);
            window.removeEventListener('resize', windowResizeEvent);

        }

        props.gameInfoLabel = null;
        props.inningsScrollLeft = null;
        props.inningsScrollRight = null;
        props.inningsContainer = null;

        if (core.gameInfoAnimationTimeout) {

            window.clearTimeout(core.gameInfoAnimationTimeout);

        }

        if (core.scrollAnimationTimer) {

            window.clearInterval(core.scrollAnimationTimer);

        }

        return this;

    }

    /**
     * @method updateScrollButtons
     * @desc Check scroll position to disable/enable scroll buttons at beginning/end
     */
    updateScrollButtons() {

        const { props } = this;
        const { componentName, inningsScrollLeft, inningsScrollRight, inningsContainer } = props;
        const { scrollLeft } = inningsContainer;
        let atLeftEnd = scrollLeft <= 1; // SSSS-4636 - IE11 bug loading with scrollLeft = 1
        let atRightEnd = scrollLeft >= props.inningsContainerMaxLeft;

        props.inningsContainerScrollLeft = scrollLeft;

        if (atLeftEnd && atRightEnd) {

            inningsScrollRight.classList.add(`${componentName}__linescore__innings-scroll--hide`);
            inningsScrollLeft.classList.add(`${componentName}__linescore__innings-scroll--hide`);

        } else {

            inningsScrollRight.classList.remove(`${componentName}__linescore__innings-scroll--hide`);
            inningsScrollLeft.classList.remove(`${componentName}__linescore__innings-scroll--hide`);

            if (atRightEnd) {

                inningsScrollRight.classList.add(`${componentName}__linescore__innings-scroll--disabled`);

            } else {

                inningsScrollRight.classList.remove(`${componentName}__linescore__innings-scroll--disabled`);

            }

            if (atLeftEnd) {

                inningsScrollLeft.classList.add(`${componentName}__linescore__innings-scroll--disabled`);

            } else {

                inningsScrollLeft.classList.remove(`${componentName}__linescore__innings-scroll--disabled`);

            }

        }
    }

    /**
     * @method resetInningsContainerMaxLeft
     * @desc On render, re-render & resize, reset point where right arrow is insensitive
     */
    resetInningsContainerMaxLeft() {

        const { props } = this;
        const { inningsContainer } = props;
        const { scrollWidth, clientWidth } = inningsContainer;

        props.inningsContainerMaxLeft = (scrollWidth - clientWidth) - 1;

    }

    /**
     * @method setInningsScrollLeft
     * @desc When component is re-rendered, remember scroll position.
     * If scroll was to last inning, scroll to new last inning.
     */
    setInningsScrollLeft() {

        const { props } = this;
        const { inningsContainer, inningsContainerScrollLeft, inningsContainerMaxLeft } = props;
        const isEnd = inningsContainerScrollLeft >= inningsContainerMaxLeft;

        this.resetInningsContainerMaxLeft();

        if (props.inningsContainerScrollLeft === undefined || isEnd) {

            props.inningsContainerScrollLeft = props.inningsContainerMaxLeft + 1;

        }

        inningsContainer.scrollLeft = props.inningsContainerScrollLeft;

    }

    /**
     * @method pauseRendering
     * @description Pauses view refresh
     */
    pauseRendering() {
        let gamePk = this.opts.gamePk;
        this.parent.emit('pause', { gamePk: gamePk });
    }

    /**
     * @method resumeRendering
     * @description Restarts view refresh
     */
    resumeRendering() {
        let gamePk = this.opts.gamePk;
        this.parent.emit('resume', { gamePk: gamePk });
    }

    // Static functions are event handlers that must be bound in constructor
    /**
     * @method toggleGameInfoEvent
     * @description Show/Hide "Game Info" section when button is clicked
     * @param event
     */
    static toggleGameInfoEvent(event = {}) {

        let { element, props, core } = this;
        let { componentName } = props;

        let vm = this.parent;
        vm.emit('toggle-game-info');

        this.pauseRendering();

        let gameInfoLabel = element.querySelector(`.${componentName}__game-info__label`);
        let gameInfoContent = element.querySelector(`.${componentName}__game-info__content`);

        //
        // toggle modifier
        //
        gameInfoContent.classList.toggle(`${componentName}__game-info__content--hide`);
        gameInfoLabel.classList.toggle(`${componentName}__game-info__label--expanded`);

        let boundResumeRendering = this.resumeRendering.bind(this);

        // duration for this timeout should match the css transition specified in less
        //@scores-game-info-content-transition--visible: max-height 0.25s ease-in;
        //@scores-game-info-content-transition--hidden: max-height 0.25s ease-out;
        core.gameInfoAnimationTimeout = window.setTimeout(boundResumeRendering, 250);

    }

    /**
     * @method scrollInningsLeftEvent
     * @description Scroll extra innings linescore to the left
     * @param event
     */
    static scrollInningsLeftEvent(event = {}) {

        let { props, core } = this;

        if (core.scrollAnimationInProgress) {

            return;

        }

        let { scrollDistance } = props;

        this.animateInningsScroll('left', scrollDistance);

    }

    /**
     * @method scrollInningsRightEvent
     * @description Scroll extra innings linescore to the right
     * @param event
     */
    static scrollInningsRightEvent(event = {}) {

        let { props, core } = this;

        if (core.scrollAnimationInProgress) {

            return;

        }
        let { scrollDistance } = props;

        this.animateInningsScroll('right', scrollDistance);

    }

    /**
     * @method inningsScrollEvent
     * @desc After innings are scrolled, update buttons
     * @param event
     */
    static inningsScrollEvent(event = {}) {

        let { core } = this;
        let { scrollThrottleEvent } = core;

        clearTimeout(scrollThrottleEvent);

        core.scrollThrottleEvent = setTimeout(() => {

            this.updateScrollButtons();

        }, 100);
    }

    /**
     * @method windowResizeEvent
     * @desc
     * @param event
     */
    static windowResizeEvent(event = {}) {

        let { core } = this;
        let { resizeThrottleEvent } = core;

        clearTimeout(resizeThrottleEvent);

        core.resizeThrottleEvent = setTimeout(() => {

            this.resetInningsContainerMaxLeft();
            this.updateScrollButtons();

        }, 100);

    }
}

/**
 *
 * @function componentFactory
 * @param {ViewModel|Object} parent
 * @returns {Component}
 *
 */
function componentFactory(parent = {}) {

    return new Component(parent);

}

export default componentFactory;
